import type React from 'react';
import { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../tailgrids/core/tooltip.js';
import { PageTitleSlotContext } from '../shell/page-title-slot.js';
import { useInEntityPanel } from '../page/entity-panel-scope.js';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { HelpPanel } from './HelpPanel.js';

export interface PageHelpContent {
  title: string;
  markdown: string;
}

interface PageTitleProps {
  title: string;
  /** One line about the page. It is the help icon's tooltip, not a line of text under the title. */
  description?: string;
  /** The page's help file; the help icon opens it in a side panel. */
  helpContent?: PageHelpContent;
  /** Status badges shown after the title. */
  badges?: React.ReactNode;
  /** A back arrow before the title (builders and other pages without breadcrumbs). */
  backTo?: string;
  backLabel?: string;
}

const HELP_ICON_CLASS =
  'flex size-8 shrink-0 items-center justify-center rounded-lg text-text-tertiary transition-colors outline-none hover:bg-background-gray-primary hover:text-text-primary focus-visible:ring-2 focus-visible:ring-primary-500';

/**
 * The page's title. On wide screens (xl+) it renders into the shell header, where the search box used to be (owner
 * 2026-10-17: "page title goes into its space, page description becomes ? icon tooltip, click stays the same"); below
 * xl the header has no room and the title stays at the top of the page. Every routed page renders one, through
 * `PageHeader` (the page templates) or directly (builders).
 */
export function PageTitle({ title, description, helpContent, badges, backTo, backLabel }: PageTitleProps) {
  const slot = useContext(PageTitleSlotContext);
  const wide = useMediaQuery('(min-width: 1280px)');
  const [helpOpen, setHelpOpen] = useState(false);
  // In a side panel the title is the panel's heading: never in the shell header, never a way out of the page.
  const inPanel = useInEntityPanel();

  const tip = description ?? (helpContent ? 'Learn more about this page' : null);

  const content = (
    <div className="flex min-w-0 items-center gap-2">
      {backTo && !inPanel && (
        <Link
          to={backTo}
          aria-label={backLabel ?? 'Back'}
          className="flex size-8 shrink-0 items-center justify-center rounded-lg text-text-tertiary transition-colors hover:bg-background-gray-primary hover:text-text-primary"
        >
          <ArrowLeft size={20} />
        </Link>
      )}
      {inPanel ? (
        <h2 className="truncate text-lg leading-7 font-medium text-text-primary">{title}</h2>
      ) : (
        <h1 className="truncate text-2xl leading-8 font-medium text-text-primary">{title}</h1>
      )}
      {tip && (
        <Tooltip placement="bottom-start">
          <TooltipTrigger
            type="button"
            aria-label={helpContent ? `Help: ${title}` : `About ${title}`}
            onClick={helpContent ? () => setHelpOpen(true) : undefined}
            className={HELP_ICON_CLASS}
          >
            <HelpCircle size={18} aria-hidden="true" />
          </TooltipTrigger>
          <TooltipContent className="max-w-xs font-normal">{tip}</TooltipContent>
        </Tooltip>
      )}
      {badges && <div className="flex shrink-0 flex-wrap items-center gap-2">{badges}</div>}
    </div>
  );

  return (
    <>
      {slot && wide && !inPanel ? createPortal(content, slot) : content}
      {helpContent && (
        <HelpPanel
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title={helpContent.title}
          markdown={helpContent.markdown}
        />
      )}
    </>
  );
}
